import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { CommonResponse } from 'app/server/models';
import { environment } from 'environments/environment';

/**
 * Servizio centralizzato per la gestione del codice fiscale italiano
 * Fornisce metodi per estrarre informazioni dal codice fiscale
 */
@Injectable({
	providedIn: 'root',
})
export class FiscalCodeService {
	constructor(private _httpClient: HttpClient) {}

	/**
	 * Estrae il sesso dal codice fiscale
	 * @param fiscalCode - Il codice fiscale
	 * @returns 'male', 'female' o null
	 */
	extractGender(fiscalCode: string): string | null {
		if (!fiscalCode || fiscalCode.length !== 16) {
			return null;
		}

		const dayDigits = fiscalCode.substring(9, 11);
		const isDayValid = /^\d+$/.test(dayDigits);

		if (isDayValid) {
			return dayDigits > '40' ? 'female' : 'male';
		}

		return null;
	}

	/**
	 * Estrae la data di nascita dal codice fiscale
	 * @param fiscalCode - Il codice fiscale
	 * @returns La data di nascita in formato YYYY-MM-DD o null
	 */
	extractDateOfBirth(fiscalCode: string): string | null {
		if (!fiscalCode || fiscalCode.length !== 16) {
			return null;
		}

		const monthMap: { [key: string]: string } = {
			A: '01',
			B: '02',
			C: '03',
			D: '04',
			E: '05',
			H: '06',
			L: '07',
			M: '08',
			P: '09',
			R: '10',
			S: '11',
			T: '12',
		};

		const yearDigits = fiscalCode.substring(6, 8);
		const monthLetter = fiscalCode.substring(8, 9);
		let dayDigits = fiscalCode.substring(9, 11);

		const isYearValid = /^\d+$/.test(yearDigits);
		const isDayValid = /^\d+$/.test(dayDigits);

		if (!isYearValid || !isDayValid) {
			return null;
		}

		// Determina il secolo
		let century: string;
		const currentYear = new Date().getFullYear();
		const birthYear = parseInt(yearDigits);
		if (birthYear <= currentYear % 100) {
			century = '20';
		} else {
			century = '19';
		}

		// Correggi il giorno per le donne (oltre 40)
		if (dayDigits > '40') {
			dayDigits = (+dayDigits - 40).toString().padStart(2, '0');
		}

		const monthDigits = monthMap[monthLetter];
		if (!monthDigits) {
			return null;
		}

		return `${century}${yearDigits}-${monthDigits}-${dayDigits}`;
	}

	/**
	 * Estrae il luogo di nascita dal codice fiscale utilizzando l'API backend
	 * @param fiscalCode - Il codice fiscale
	 * @returns Observable che emette il nome del comune o null se non trovato
	 */
	extractBirthPlace(fiscalCode: string): Observable<string | null> {
		if (!fiscalCode || fiscalCode.length !== 16) {
			return of(null);
		}

		// Estrai il codice catastale (caratteri da 11 a 14)
		const cadastralCode = fiscalCode.substring(11, 15).toUpperCase();

		return this._httpClient
			.get<CommonResponse<{ name: string }>>(`${environment.apiUrl}/fiscal-code/check-birth-place/${cadastralCode}`)
			.pipe(
				map(response => {
					if (response.success && response.data) {
						return response.data.name;
					}
					return null;
				}),
				catchError(() => of(null))
			);
	}

	/**
	 * Valida il formato del codice fiscale italiano
	 * @param fiscalCode - Il codice fiscale da validare
	 * @returns true se il formato è valido, false altrimenti
	 */
	validateFiscalCode(fiscalCode: string): boolean {
		if (!fiscalCode || fiscalCode.length !== 16) {
			return false;
		}

		// Regex per validazione codice fiscale italiano
		// http://blog.marketto.it/2016/01/regex-validazione-codice-fiscale-con-omocodia/
		const fiscalCodeRegex = /^[A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]$/i;

		return fiscalCodeRegex.test(fiscalCode);
	}

	/**
	 * Estrae tutte le informazioni disponibili dal codice fiscale
	 * @param fiscalCode - Il codice fiscale
	 * @returns Observable con tutte le informazioni estratte
	 */
	extractAllInfo(fiscalCode: string): Observable<{
		gender: string | null;
		dateOfBirth: string | null;
		birthPlace: string | null;
		isValid: boolean;
	}> {
		return this.extractBirthPlace(fiscalCode).pipe(
			map(birthPlace => ({
				gender: this.extractGender(fiscalCode),
				dateOfBirth: this.extractDateOfBirth(fiscalCode),
				birthPlace: birthPlace,
				isValid: this.validateFiscalCode(fiscalCode),
			}))
		);
	}
}
